// Shared font-family stacks for the editor.
//
// Two consumers:
//   1. The docx import path (serializers/ooxml) — a .docx run carries a BARE
//      face name (`Calibri`, `Cambria`, …). We map it to a full CSS stack with
//      metric-close fallbacks so the text still renders sensibly on a machine
//      that doesn't have the exact face installed (macOS has no Calibri), and so
//      the FontFamilyMenu can match the saved mark back to a picker option.
//   2. The FontFamilyMenu picker — its options reuse these same stacks, so an
//      imported `Calibri` leaf lights up the "Calibri" option (matchOption
//      compares the first family token).
//
// Keep the KEY lowercased and quote multi-word faces in the stack.

export const WORD_FONT_STACKS: Record<string, string> = {
    // Brand + app fonts (kept here so the picker and import share one source).
    gilroy: '"Gilroy-Regular", "Gilroy", -apple-system, BlinkMacSystemFont, sans-serif',
    inter: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',

    // Microsoft Office defaults + common Word faces. Calibri/Cambria/Aptos are
    // not installed on macOS; Carlito (→Calibri) and Caladea (→Cambria) are the
    // metric-compatible substitutes — listed so a machine that DOES have them
    // renders identically, with system sans/serif as the final fallback.
    calibri: '"Calibri", "Carlito", "Segoe UI", Helvetica, Arial, sans-serif',
    'calibri light': '"Calibri Light", "Calibri", "Carlito", "Segoe UI", Helvetica, Arial, sans-serif',
    cambria: '"Cambria", "Caladea", Georgia, "Times New Roman", serif',
    aptos: '"Aptos", "Calibri", "Segoe UI", Helvetica, Arial, sans-serif',
    'aptos display': '"Aptos Display", "Aptos", "Calibri", "Segoe UI", Helvetica, Arial, sans-serif',
    'segoe ui': '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif',

    // Cross-platform web-safe faces.
    arial: 'Arial, Helvetica, sans-serif',
    helvetica: 'Helvetica, Arial, sans-serif',
    georgia: 'Georgia, "Times New Roman", serif',
    'times new roman': '"Times New Roman", Times, serif',
    'courier new': '"Courier New", Courier, monospace',
    verdana: 'Verdana, Geneva, sans-serif',
    tahoma: 'Tahoma, Geneva, sans-serif',
    'trebuchet ms': '"Trebuchet MS", Tahoma, sans-serif',
    'palatino linotype': '"Palatino Linotype", "Book Antiqua", Palatino, serif',
    garamond: 'Garamond, "EB Garamond", "Times New Roman", serif',
    'comic sans ms': '"Comic Sans MS", "Comic Sans", cursive',
};

// Faces whose name signals a serif/monospace family, so an UNKNOWN font gets a
// sensible generic tail instead of always defaulting to sans-serif.
const SERIF_HINT = /(times|serif|roman|georgia|garamond|cambria|minion|book antiqua|palatino|caslon|baskerville|ming|song|sung)/i;
const MONO_HINT = /(mono|courier|consol|menlo|code|typewriter)/i;

function normalizeKey(name: string): string {
    return name
        .trim()
        .replace(/^['"]+|['"]+$/g, '')
        .toLowerCase();
}

/**
 * Map a bare font-face name to a full CSS font-family stack.
 * - A known Office/web face → its curated stack (metric-close fallbacks).
 * - Any other face → the requested face first, then a generic family guessed
 *   from the name (serif / monospace / sans-serif).
 * Returns undefined for empty/nullish input.
 */
export function fontNameToStack(name?: string | null): string | undefined {
    if (!name) return undefined;
    const key = normalizeKey(name);
    if (!key) return undefined;
    if (Object.prototype.hasOwnProperty.call(WORD_FONT_STACKS, key)) return WORD_FONT_STACKS[key];
    const generic = MONO_HINT.test(key) ? 'monospace' : SERIF_HINT.test(key) ? 'serif' : 'sans-serif';
    // Quote the face so multi-word names stay valid CSS.
    return `"${name.trim().replace(/^['"]+|['"]+$/g, '')}", ${generic}`;
}

/** The first family token of a stack, lowercased/unquoted — used to match marks. */
export function fontStackHead(stack?: string | null): string {
    if (!stack) return '';
    return stack.split(',')[0]?.replace(/['"]/g, '').trim().toLowerCase() ?? '';
}

// CSS keywords that resolve to *whatever font the OS/browser picks* rather than
// naming an actual installable face — e.g. the FontFamilyMenu "System"/
// "Monospace" picker options lead their stack with these so the editor renders
// with the native UI font. They're meaningless as a .docx run's `font` name.
const SYSTEM_FONT_KEYWORDS = new Set(['-apple-system', 'blinkmacsystemfont', 'system-ui', 'ui-monospace', 'ui-serif', 'ui-rounded']);
const GENERIC_FONT_FAMILIES = new Set(['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy']);
// Word's own default typeface — used only when a stack is nothing BUT system/
// generic tokens (no picker stack currently is; kept as a safety net for a
// future one that might be).
const WORD_SAFE_FALLBACK_FACE = 'Calibri';

/**
 * The first REAL face name of a stack, unquoted and in its ORIGINAL case — for
 * writing a single face name back to a format that wants one (e.g. .docx export,
 * where the run's `font` must be a face name like "Calibri", not a CSS stack).
 * Skips system-font keywords and generic families (see SYSTEM_FONT_KEYWORDS
 * above) so the "System"/"Monospace" picker options — whose stacks lead with
 * `-apple-system`/`ui-monospace` — export their first real fallback face
 * (e.g. "Segoe UI") instead of that meaningless keyword.
 */
export function fontStackFace(stack?: string | null): string | undefined {
    if (!stack) return undefined;
    const tokens = stack
        .split(',')
        .map((t) => t.replace(/['"]/g, '').trim())
        .filter(Boolean);
    const realFace = tokens.find((t) => !SYSTEM_FONT_KEYWORDS.has(t.toLowerCase()) && !GENERIC_FONT_FAMILIES.has(t.toLowerCase()));
    return realFace ?? (tokens.length > 0 ? WORD_SAFE_FALLBACK_FACE : undefined);
}
